/* global growtype_ajax_url */

import { safeStorage } from '../helpers/safeStorage';
import {
    createStoryWatchSession,
    getStoryWatchTimeMs,
    pauseStoryWatch,
    resumeStoryWatch,
    summarizeStoryWatch,
    updateStoryProgress
} from './storiesTracking';

/**
 * Stories Slider Component
 * Handles: slider initialization, lazy video loading, fullscreen modal with infinite scroll
 */

// Change only this constant to enable or disable the complete Stories trace.
const STORIES_DEBUG_ENABLED = false;
let storiesDebugSequence = 0;
const storiesDebugStartedAt = performance.now();

function storiesDebug(step, details = {}) {
    if (!STORIES_DEBUG_ENABLED) return;

    console.log(`[StoriesDebug #${++storiesDebugSequence}] ${step}`, {
        elapsed_ms: Math.round((performance.now() - storiesDebugStartedAt) * 10) / 10,
        current_index: StoriesState?.currentIndex ?? null,
        story_id: new URL(window.location.href).searchParams.get('story_id') || '',
        ...details
    });
}

// Global state
const StoriesState = {
    currentIndex: 0,
    stories: [],
    loadedIds: [],
    isLoading: false,
    hasMore: true,
    loadMoreLimit: 12,
    lastRequestedCursor: null,
    ajaxUrl: typeof growtype_ajax_url !== 'undefined' ? growtype_ajax_url : '/wp/wp-admin/admin-ajax.php',
    observer: null, // IntersectionObserver instance
    watchedIds: JSON.parse(safeStorage.local.get('growtype_watched_stories') || '[]').map(String),
    likedIds: JSON.parse(safeStorage.local.get('growtype_liked_stories') || '[]').map(String),
    dislikedIds: JSON.parse(safeStorage.local.get('growtype_disliked_stories') || '[]').map(String),
    pendingLikes: {},
    likeSyncTimers: {}, // { charId: timeoutId }
    pendingDislikes: {},
    dislikeSyncTimers: {}, // { charId: timeoutId }
    isMuted: true,
    type: 'bubbles',
    trackedImpressionIds: [],
    sliderImpressionTracked: false,
    sliderImpressionObserver: null,
    modalSessionId: '',
    modalOpenedAt: 0,
    modalViewedIds: [],
    activeView: null,
    lastViewedStoryId: '',
    pendingNavigationMethod: 'open',
    watchTimers: {},
    lifecycleTrackingInitialized: false,
    deepLinkOpened: false
};

function storyIdentity(story) {
    return String(story?.story_id || story?.character?.id || '');
}

function storyVariants(story) {
    const variants = Array.isArray(story?.story_variants) ? story.story_variants : [];
    return variants.length > 0 ? variants : [{
        story_id: storyIdentity(story),
        video: story?.video || {},
        poster: story?.poster || ''
    }];
}

function storyGroupByIdentity(storyId) {
    const requestedId = String(storyId || '');
    return StoriesState.stories.find(story => (
        storyIdentity(story) === requestedId
        || storyVariants(story).some(variant => storyIdentity(variant) === requestedId)
    )) || null;
}

function storyFromVariant(group, variant) {
    return group && variant ? { ...group, ...variant, story_variants: group.story_variants } : null;
}

function storyGroupIndex(story) {
    const storyId = storyIdentity(story);
    return StoriesState.stories.findIndex(candidate => (
        storyIdentity(candidate) === storyId
        || storyVariants(candidate).some(variant => storyIdentity(variant) === storyId)
    ));
}

function appendStoryVariantIds(story, target = StoriesState.loadedIds) {
    storyVariants(story).forEach(variant => {
        const id = storyIdentity(variant);
        if (id && !target.includes(id)) target.push(id);
    });
}

function storyIdFromUrl() {
    const storyId = new URL(window.location.href).searchParams.get('story_id') || '';
    storiesDebug('url.read', { resolved_story_id: storyId });
    return storyId;
}

function updateStoryUrl(storyId) {
    if (!storyId) {
        storiesDebug('url.update.skipped', { reason: 'empty_story_id' });
        return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get('story_id') === storyId) {
        storiesDebug('url.update.skipped', { reason: 'already_current', requested_story_id: storyId });
        return;
    }
    const previousStoryId = url.searchParams.get('story_id') || '';
    url.searchParams.set('story_id', storyId);
    window.history.replaceState(window.history.state, document.title, url.toString());
    storiesDebug('url.updated', { previous_story_id: previousStoryId, next_story_id: storyId });
}

function clearStoryUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('story_id')) {
        storiesDebug('url.clear.skipped', { reason: 'missing_story_id' });
        return;
    }
    const previousStoryId = url.searchParams.get('story_id') || '';
    url.searchParams.delete('story_id');
    window.history.replaceState(window.history.state, document.title, url.toString());
    storiesDebug('url.cleared', { previous_story_id: previousStoryId });
}

function dismissStoriesHint() {
    document.getElementById('storiesScrollHint')?.classList.remove('active');
    safeStorage.local.set('growtype_stories_hint_shown_v2', 'true');
}

function waitForStoryTransitionSpinner() {
    return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
}

function showStoryTransitionSpinner(slide) {
    if (!slide) {
        storiesDebug('spinner.skipped', { reason: 'missing_slide' });
        return null;
    }

    const loader = slide.querySelector('.fullscreen-slide-loader');
    slide.querySelector('video')?.classList.remove('is-ready');
    loader?.classList.add('story-transition-loading');
    storiesDebug('spinner.shown', {
        slide_story_id: slide.dataset.storyId || '',
        loader_found: Boolean(loader)
    });
    return loader;
}

const STORIES_EVENT_PREFIX = 'growtype_analytics_growtype_child_stories_';
const STORIES_SLIDER_SELECTOR = '.growtype-theme-slider, .stories-bubble-scroller';
const STORIES_READ_MODEL_URL = '/api/v2/stories';

function storyMatchesIdentity(story, requestedId) {
    const identity = String(requestedId || '');
    return storyIdentity(story) === identity
        || storyVariants(story).some(variant => storyIdentity(variant) === identity);
}

function personalizeStoryReadModel(items, limit) {
    const watched = new Set(StoriesState.watchedIds.map(String));
    const liked = new Set(StoriesState.likedIds.map(String));
    const disliked = new Set(StoriesState.dislikedIds.map(String));

    return items
        .filter(story => {
            const characterId = String(story?.character?.id || story?.character?.external_id || '');
            return !disliked.has(storyIdentity(story)) && !disliked.has(characterId);
        })
        .map((story, position) => {
            const characterId = String(story?.character?.id || story?.character?.external_id || '');
            return {
                story: {
                    ...story,
                    is_liked: liked.has(storyIdentity(story)) || liked.has(characterId)
                },
                position,
                watched: storyVariants(story).every(variant => watched.has(storyIdentity(variant)))
            };
        })
        .sort((left, right) => Number(left.watched) - Number(right.watched) || left.position - right.position)
        .slice(0, Math.max(1, Math.min(15, Number(limit) || 15)))
        .map(entry => entry.story);
}

function normalizeStoriesReadModel(document, requestedStoryId, limit) {
    if (document?.error !== null
        || document?.meta?.version !== 2
        || document?.meta?.resource !== 'story_collection'
        || document?.data?.type !== 'story_collection'
        || typeof document?.data?.shell !== 'string'
        || !Array.isArray(document?.data?.items)) {
        throw new Error('Invalid stories read model.');
    }
    const stories = personalizeStoryReadModel(document.data.items, limit);
    if (stories.length === 0
        || (requestedStoryId && !stories.some(story => storyMatchesIdentity(story, requestedStoryId)))) {
        throw new Error('Requested story is not in the published read model.');
    }
    return { success: true, data: { html: document.data.shell, stories } };
}

function createTrackingId(prefix) {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function storyByIdentity(storyId) {
    const requestedId = String(storyId || '');
    const group = storyGroupByIdentity(requestedId);
    if (!group) return null;
    const variant = storyVariants(group).find(candidate => storyIdentity(candidate) === requestedId);
    return variant ? storyFromVariant(group, variant) : group;
}

function storyContextFromElement(element, fallbackCharacterId = '') {
    const slide = element?.closest?.('.fullscreen-story, .story-slide');
    const storyId = String(slide?.dataset.storyId || '');
    let story = storyByIdentity(storyId);
    if (!story && fallbackCharacterId) {
        story = StoriesState.stories.find(candidate => (
            String(candidate?.character?.id || candidate?.character?.external_id || '') === String(fallbackCharacterId)
        )) || null;
    }

    return {
        story,
        index: story ? storyGroupIndex(story) : -1
    };
}

function storyTrackingMetadata(story, index, extra = {}) {
    const storyId = storyIdentity(story);
    const character = story?.character || {};
    const resolvedIndex = Number.isInteger(index)
        ? index
        : storyGroupIndex(story);

    return {
        story_id: storyId,
        character_id: String(character.id || character.external_id || ''),
        character_slug: String(character.slug || ''),
        story_position: resolvedIndex >= 0 ? resolvedIndex + 1 : null,
        slider_type: StoriesState.type,
        is_locked: Boolean(character.is_locked),
        modal_session_id: StoriesState.modalSessionId || '',
        ...extra
    };
}

function trackStoriesEvent(action, story = null, index = null, extra = {}) {
    const eventName = `${STORIES_EVENT_PREFIX}${action}`;
    const metadata = storyTrackingMetadata(story, index, extra);

    if (typeof window.growtypeAnalyticsCapture === 'function') {
        window.growtypeAnalyticsCapture(eventName, metadata);
    }

    // Store the same privacy-safe event in the first-party analytics table so
    // Stories value can be evaluated without depending on a third-party tool.
    if (typeof window.gaTrack === 'function') {
        window.gaTrack(eventName, {
            object_id: metadata.story_id || metadata.character_id || '',
            object_type: metadata.story_id ? 'story' : 'stories',
            metadata
        });
    }
}

function setStoriesChatAttribution(story, index) {
    const character = story?.character || {};
    const details = {
        recommendation_rule: 'stories_engagement',
        recommendation_rule_version: 'v1',
        recommendation_source: 'stories',
        recommendation_position: Number.isInteger(index) ? index + 1 : '',
        recommended_character_id: String(character.id || character.external_id || ''),
        recommended_character_slug: String(character.slug || '')
    };
    const expires = new Date(Date.now() + (30 * 86400000)).toUTCString();
    document.cookie = 'ga_attribution_context=' + encodeURIComponent(JSON.stringify(details))
        + '; path=/; expires=' + expires + '; SameSite=Lax';
}

function trackVisibleStoryImpressions($slider) {
    if (!StoriesState.sliderImpressionTracked) return;

    const sliderElement = $slider[0];
    const visibleSlides = $slider.hasClass('stories-bubble-scroller')
        ? $slider.children('.story-slide').filter(function () {
            const sliderRect = sliderElement.getBoundingClientRect();
            const slideRect = this.getBoundingClientRect();
            return slideRect.right > sliderRect.left && slideRect.left < sliderRect.right;
        })
        : $slider.find('.slick-slide.slick-active:not(.slick-cloned)');

    visibleSlides.each(function () {
        const storyId = String(this.dataset.storyId || '');
        if (!storyId || StoriesState.trackedImpressionIds.includes(storyId)) return;

        StoriesState.trackedImpressionIds.push(storyId);
        const story = storyByIdentity(storyId);
        trackStoriesEvent('story_impression', story, StoriesState.stories.indexOf(story));
    });
}

function setupSliderImpressionTracking($html, $slider) {
    const element = $html[0];
    if (!element) return;

    const recordImpression = () => {
        if (!StoriesState.sliderImpressionTracked) {
            StoriesState.sliderImpressionTracked = true;
            trackStoriesEvent('slider_impression', null, null, {
                story_count: StoriesState.stories.length
            });
        }
        trackVisibleStoryImpressions($slider);
    };

    if (!('IntersectionObserver' in window)) {
        recordImpression();
        return;
    }

    if (StoriesState.sliderImpressionObserver) StoriesState.sliderImpressionObserver.disconnect();
    StoriesState.sliderImpressionObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
            recordImpression();
            StoriesState.sliderImpressionObserver.disconnect();
        }
    }, { threshold: 0.5 });
    StoriesState.sliderImpressionObserver.observe(element);
}

function clearStoryWatchTimer(storyId) {
    const timer = StoriesState.watchTimers[String(storyId || '')];
    if (timer) clearTimeout(timer);
    delete StoriesState.watchTimers[String(storyId || '')];
}

function scheduleWatchedStory(slide) {
    const activeView = StoriesState.activeView;
    const storyId = String(slide?.dataset.storyId || slide?.dataset.characterId || '');
    clearStoryWatchTimer(storyId);
    if (!activeView || activeView.slide !== slide || activeView.watch.playingStartedAtMs === null) return;

    const remainingMs = Math.max(0, 1000 - getStoryWatchTimeMs(activeView.watch, performance.now()));
    if (remainingMs === 0) {
        markAsWatched(storyId);
        return;
    }

    StoriesState.watchTimers[storyId] = setTimeout(() => {
        if (StoriesState.activeView?.slide === slide) scheduleWatchedStory(slide);
    }, remainingMs);
}

function startStoryView(slide, video) {
    const storyId = String(slide?.dataset.storyId || slide?.dataset.characterId || '');
    if (!storyId || StoriesState.activeView?.storyId === storyId) return;

    if (StoriesState.activeView) endActiveStoryView('story_changed');

    const story = storyByIdentity(storyId);
    const index = storyGroupIndex(story);
    const navigationMethod = StoriesState.pendingNavigationMethod || 'scroll';

    if (StoriesState.lastViewedStoryId && StoriesState.lastViewedStoryId !== storyId) {
        trackStoriesEvent('story_changed', story, index, {
            previous_story_id: StoriesState.lastViewedStoryId,
            navigation_method: navigationMethod
        });
    }

    StoriesState.activeView = {
        storyId,
        story,
        index,
        slide,
        video,
        viewId: createTrackingId('story-view'),
        navigationMethod,
        watch: createStoryWatchSession(performance.now())
    };
    StoriesState.lastViewedStoryId = storyId;
    StoriesState.pendingNavigationMethod = 'scroll';
    updateStoryUrl(storyId);

    trackStoriesEvent('story_view_started', story, index, {
        story_view_id: StoriesState.activeView.viewId,
        navigation_method: navigationMethod
    });
    if (video && !video.paused) resumeStoryWatch(StoriesState.activeView.watch, performance.now());
}

function endActiveStoryView(exitReason) {
    const activeView = StoriesState.activeView;
    if (!activeView) return;

    const now = performance.now();
    const videoDuration = Number.isFinite(activeView.video?.duration) ? activeView.video.duration : 0;
    const summary = summarizeStoryWatch(activeView.watch, now, videoDuration);

    trackStoriesEvent('story_view_ended', activeView.story, activeView.index, {
        story_view_id: activeView.viewId,
        navigation_method: activeView.navigationMethod,
        exit_reason: exitReason,
        ...summary
    });
    clearStoryWatchTimer(activeView.storyId);
    StoriesState.activeView = null;
}

function trackStoryProgress(video) {
    const activeView = StoriesState.activeView;
    if (!activeView || activeView.video !== video) return;

    const milestones = updateStoryProgress(activeView.watch, video.currentTime, video.duration);
    milestones.forEach(milestone => {
        trackStoriesEvent('story_progress', activeView.story, activeView.index, {
            story_view_id: activeView.viewId,
            milestone_percent: milestone,
            video_duration_seconds: Number.isFinite(video.duration) ? Math.round(video.duration * 100) / 100 : 0
        });
    });
}

// Slick may be registered on WordPress' jQuery while this bundle uses its own
// jQuery instance. Capture the registered plugin and keep fullscreen usable if
// the dependency is later unavailable or the slider instance is stale.
const storiesSlickPlugin = typeof window.jQuery?.fn?.slick === 'function'
    ? window.jQuery.fn.slick
    : null;

function getStoriesSlickPlugin() {
    if (typeof $.fn?.slick === 'function') {
        return $.fn.slick;
    }

    return storiesSlickPlugin;
}

function callStoriesSlick($slider, command, ...args) {
    const plugin = getStoriesSlickPlugin();
    if (!$slider?.length || typeof plugin !== 'function') {
        return undefined;
    }

    let $target = $slider;
    if (typeof command === 'string') {
        $target = $slider.filter(function () {
            return this.slick && typeof this.slick[command] === 'function';
        });

        if (!$target.length) {
            return undefined;
        }
    }

    return plugin.apply($target, [command, ...args]);
}

function activateStoryPoster(image, highPriority = false) {
    if (!image || image.getAttribute('src')) return;

    const srcset = image.getAttribute('data-srcset');
    const sizes = image.getAttribute('data-sizes');
    const src = image.getAttribute('data-src');
    const fallbackSrc = image.getAttribute('data-fallback-src');
    const defaultSrc = image.getAttribute('data-default-src');
    let fallbackAttempted = false;
    let defaultAttempted = false;

    const handleError = () => {
        image.removeAttribute('srcset');
        image.removeAttribute('data-srcset');

        const currentSrc = image.getAttribute('src');
        if (!fallbackAttempted && fallbackSrc && fallbackSrc !== currentSrc) {
            fallbackAttempted = true;
            image.setAttribute('src', fallbackSrc);
            return;
        }

        if (!defaultAttempted && defaultSrc && defaultSrc !== currentSrc) {
            defaultAttempted = true;
            image.setAttribute('src', defaultSrc);
            return;
        }

        image.removeEventListener('error', handleError);
        image.removeAttribute('src');
        image.classList.add('is-unavailable');
    };

    image.addEventListener('error', handleError);
    image.addEventListener('load', () => {
        image.removeEventListener('error', handleError);
    }, { once: true });

    if (srcset) image.setAttribute('srcset', srcset);
    if (sizes) image.setAttribute('sizes', sizes);

    if (highPriority) {
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', 'high');
    } else {
        image.setAttribute('loading', 'lazy');
        image.setAttribute('fetchpriority', 'auto');
    }

    if (src) image.setAttribute('src', src);
}

/**
 * Load only currently visible story posters. Posters farther away retain
 * data-src and consume no bandwidth until horizontal navigation reveals them.
 */
function loadNearbyStoryPosters($sliders) {
    $sliders.each(function () {
        const slider = this;
        if (slider.classList.contains('stories-bubble-scroller')) {
            const slides = [...slider.querySelectorAll(':scope > .story-slide')];
            const sliderRect = slider.getBoundingClientRect();
            const firstVisibleIndex = slides.findIndex(slide => {
                const rect = slide.getBoundingClientRect();
                return rect.right > sliderRect.left && rect.left < sliderRect.right;
            });
            if (firstVisibleIndex < 0) return;

            const visibleCount = slides.filter(slide => {
                const rect = slide.getBoundingClientRect();
                return rect.right > sliderRect.left && rect.left < sliderRect.right;
            }).length;
            let priorityAssigned = false;
            slides.slice(firstVisibleIndex, firstVisibleIndex + visibleCount).forEach(slide => {
                const image = slide.querySelector('img.story-video[data-src]');
                if (!image) return;
                activateStoryPoster(image, !priorityAssigned);
                priorityAssigned = true;
            });
            return;
        }

        const activeSlides = [...slider.querySelectorAll('.slick-slide.slick-active')];
        if (activeSlides.length === 0) return;

        const activeIndexes = activeSlides
            .map(slide => Number.parseInt(slide.getAttribute('data-slick-index'), 10))
            .filter(Number.isFinite);
        const lastActiveIndex = activeIndexes.length > 0 ? Math.max(...activeIndexes) : null;
        const slidesToLoad = new Set(activeSlides);

        if (lastActiveIndex !== null) {
            [1, 2].forEach(offset => {
                const nextSlide = slider.querySelector(`.slick-slide[data-slick-index="${lastActiveIndex + offset}"]`);
                if (nextSlide) slidesToLoad.add(nextSlide);
            });
        }

        let priorityAssigned = false;
        slidesToLoad.forEach(slide => {
            const image = slide.querySelector('img.story-video[data-src]');
            if (!image) return;

            const highPriority = slide.classList.contains('slick-active') && !priorityAssigned;
            activateStoryPoster(image, highPriority);
            if (highPriority) priorityAssigned = true;
        });
    });
}

function markAsWatched(storyId) {
    if (!storyId) return;
    storyId = String(storyId);

    // Add to local state/storage
    if (!StoriesState.watchedIds.includes(storyId)) {
        StoriesState.watchedIds.push(storyId);
        safeStorage.local.set('growtype_watched_stories', JSON.stringify(StoriesState.watchedIds));

        // If logged in, also sync with server
        // We can check body class 'logged-in' or global var
        if (document.body.classList.contains('logged-in')) {
            const formData = new FormData();
            formData.append('action', 'growtype_record_story_view');
            formData.append('story_id', storyId);
            navigator.sendBeacon(StoriesState.ajaxUrl, formData);
        }
    }
}

// ... (initFullscreenFunctions and other parts remain same) ...

/**
 * Load more stories via AJAX
 */
function loadMoreStories(source = 'fullscreen') {
    storiesDebug('pagination.requested', {
        source,
        is_loading: StoriesState.isLoading,
        has_more: StoriesState.hasMore,
        stories_count: StoriesState.stories.length,
        loaded_ids_count: StoriesState.loadedIds.length
    });
    if (source !== 'fullscreen' && source !== 'horizontal-user') {
        storiesDebug('pagination.skipped', { reason: 'invalid_source', source });
        return;
    }
    if (StoriesState.isLoading) {
        storiesDebug('pagination.skipped', { reason: 'already_loading' });
        return;
    }
    if (!StoriesState.hasMore) {
        storiesDebug('pagination.exhausted');

        const container = document.getElementById('storiesFullscreenContainer');
        if (container && !container.querySelector('.no-more-stories-msg')) {
            const endMsg = document.createElement('div');
            endMsg.className = 'fullscreen-story fullscreen-loading no-more-stories-msg';
            endMsg.innerHTML = '<div style="color:#fff;font-size:16px;">You\'ve seen all the stories!</div>';
            container.appendChild(endMsg);
            // endMsg.scrollIntoView({ behavior: 'smooth' });
        }
        return;
    }

    const requestCursor = `${StoriesState.stories.length}:${StoriesState.loadedIds.length}`;
    if (StoriesState.lastRequestedCursor === requestCursor) {
        storiesDebug('pagination.skipped', { reason: 'duplicate_cursor', request_cursor: requestCursor });
        return;
    }

    StoriesState.isLoading = true;
    StoriesState.lastRequestedCursor = requestCursor;
    storiesDebug('pagination.started', { request_cursor: requestCursor });

    const container = document.getElementById('storiesFullscreenContainer');
    let loader = null;

    if (container) {
        // Show loading spinner for fullscreen
        loader = container.querySelector('.fullscreen-loading:not(.no-more-stories-msg)');
        if (!loader) {
            loader = document.createElement('div');
            loader.className = 'fullscreen-story fullscreen-loading';
            loader.innerHTML = '<div class="spinner"></div>';
            container.appendChild(loader);
        }
    }
    // loader.scrollIntoView({ behavior: 'smooth' });
    // loader.scrollIntoView({ behavior: 'smooth' });

    const excludeIds = StoriesState.loadedIds.join(',');


    fetch(StoriesState.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=growtype_child_load_more_stories&limit=${StoriesState.loadMoreLimit}&exclude=${excludeIds}&offset=${StoriesState.stories.length}&watched_history=${StoriesState.watchedIds.join(',')}&disliked_history=${StoriesState.dislikedIds.join(',')}&liked_history=${StoriesState.likedIds.join(',')}`
    })
        .then(res => {
            storiesDebug('pagination.response', { status: res.status, ok: res.ok });
            return res.json();
        })
        .then(data => {
            if (loader) loader.remove();

            if (data.success && data.data.stories?.length > 0) {
                const newStories = data.data.stories;
                storiesDebug('pagination.payload', { received_count: newStories.length });


                let addedCount = 0;
                newStories.forEach(story => {
                    const id = storyIdentity(story);

                    if (id && !StoriesState.loadedIds.includes(id)) {
                        StoriesState.loadedIds.push(id);
                        appendStoryVariantIds(story);
                        StoriesState.stories.push(story);
                        if (container) container.appendChild(createSlide(story, StoriesState.stories.length - 1));

                        // Add to horizontal slider
                        const $slider = $('.growtype-stories-slider-container').find(STORIES_SLIDER_SELECTOR);
                        if ($slider.length && $slider.hasClass('slick-initialized')) {
                            const slideHtml = createHorizontalSlideHtml(story, StoriesState.stories.length - 1);
                            callStoriesSlick($slider, 'slickAdd', slideHtml);
                        } else if ($slider.hasClass('stories-bubble-scroller')) {
                            $slider.append(createHorizontalSlideHtml(story, StoriesState.stories.length - 1));
                        }

                        addedCount++;
                    }
                });



                if (addedCount > 0) {
                    storiesDebug('pagination.appended', {
                        added_count: addedCount,
                        stories_count: StoriesState.stories.length
                    });
                    // Removed auto-jump to avoid interrupting user viewing
                    // The user will scroll naturally to new stories
                    loadNearbyStoryPosters($('.growtype-stories-slider-container').find(STORIES_SLIDER_SELECTOR));
                } else {
                    storiesDebug('pagination.empty_after_deduplication');
                    StoriesState.hasMore = false;
                    loadMoreStories(); // Trigger end message
                }

                // Check reset
                if (data.data.reset_history) {

                    StoriesState.watchedIds = [];
                    safeStorage.local.remove('growtype_watched_stories');
                }

            } else {
                storiesDebug('pagination.no_more_results', { success: Boolean(data.success) });
                StoriesState.hasMore = false;

                if (container) {
                    const endMsg = document.createElement('div');
                    endMsg.className = 'fullscreen-story fullscreen-loading no-more-stories-msg';
                    endMsg.innerHTML = '<div style="color:#fff;font-size:16px;">No more stories</div>';
                    container.appendChild(endMsg);
                }
            }

            StoriesState.isLoading = false;
            storiesDebug('pagination.finished', { has_more: StoriesState.hasMore });
        })
        .catch(err => {
            storiesDebug('pagination.failed', { message: err?.message || String(err) });
            if (loader) loader.remove();
            if (StoriesState.lastRequestedCursor === requestCursor) {
                StoriesState.lastRequestedCursor = null;
            }
            StoriesState.isLoading = false;
        });
}

// Make state accessible globally for inline onclick handlers
window.StoriesState = StoriesState;

/**
 * Initialize stories slider via AJAX
 */
function storiesSlider() {
    storiesDebug('slider.bootstrap', {
        placeholder_count: $('.growtype-stories-slider-placeholder').length
    });
    $('.growtype-stories-slider-placeholder').each(function () {
        const $container = $(this);
        const action = $container.data('action');
        const limit = $container.data('limit');
        const type = $container.data('type') || 'bubbles';
        const requestedStoryId = storyIdFromUrl();

        if (!action) {
            storiesDebug('slider.ajax.skipped', { reason: 'missing_action' });
            return;
        }

        const loadStories = () => {
            if ($container.data('stories-load-started')) return;
            $container.data('stories-load-started', true);
            storiesDebug('slider.read_model.started', { action, limit, type, requested_story_id: requestedStoryId });

            const renderStories = response => {
                storiesDebug('slider.read_model.success', {
                    success: Boolean(response.success),
                    stories_count: response.data?.stories?.length || 0,
                    has_html: Boolean(response.data?.html)
                });
                if (response.success && response.data.html) {
                    const $html = $(response.data.html);
                    $container.replaceWith($html);

                    // Store stories data
                    if (response.data.stories && response.data.stories.length > 0) {
                        StoriesState.type = type; // Store type
                        StoriesState.stories = response.data.stories;
                        StoriesState.loadedIds = response.data.stories.map(storyIdentity).filter(Boolean);
                        StoriesState.stories.forEach(story => appendStoryVariantIds(story));
                        StoriesState.lastRequestedCursor = null;
                        const $slider = $html.find(STORIES_SLIDER_SELECTOR);
                        response.data.stories.forEach((story, index) => {
                            $slider.append(createHorizontalSlideHtml(story, index));
                        });
                    }

                    // Initialize slider
                    initSlider($html);
                    setupSliderImpressionTracking($html, $html.find(STORIES_SLIDER_SELECTOR));

                    // Initialize fullscreen functions
                    initFullscreenFunctions();
                    storiesDebug('slider.fullscreen.initialized');

                    if (requestedStoryId && !StoriesState.deepLinkOpened && storyByIdentity(requestedStoryId)) {
                        StoriesState.deepLinkOpened = true;
                        storiesDebug('slider.deep_link.opening', { requested_story_id: requestedStoryId });
                        window.openStoriesFullscreen(requestedStoryId);
                    }
                } else {
                    $container.remove();
                }
            };

            const loadLegacyFallback = () => new Promise((resolve, reject) => {
                storiesDebug('slider.ajax.fallback.started', { action, requested_story_id: requestedStoryId });
                $.ajax({
                    url: StoriesState.ajaxUrl,
                    type: 'POST',
                    data: {
                        action,
                        limit,
                        type,
                        watched_history: StoriesState.watchedIds.join(','),
                        disliked_history: StoriesState.dislikedIds.join(','),
                        liked_history: StoriesState.likedIds.join(','),
                        story_id: requestedStoryId
                    },
                    success: resolve,
                    error: reject
                });
            });

            fetch(STORIES_READ_MODEL_URL, {
                method: 'GET',
                credentials: 'same-origin',
                headers: { Accept: 'application/json' }
            })
                .then(response => {
                    if (!response.ok) throw new Error(`Stories API returned HTTP ${response.status}.`);
                    return response.json();
                })
                .then(document => normalizeStoriesReadModel(document, requestedStoryId, limit))
                .catch(error => {
                    storiesDebug('slider.read_model.fallback', { message: error?.message || String(error) });
                    return loadLegacyFallback();
                })
                .then(renderStories)
                .catch(error => {
                    storiesDebug('slider.load.failed', { message: error?.message || String(error) });
                    $container.remove();
                });
        };

        if (requestedStoryId || !('IntersectionObserver' in window)) {
            loadStories();
            return;
        }

        const scheduleStoriesLoad = () => {
            const start = () => {
                window.setTimeout(() => {
                    if (typeof window.requestIdleCallback === 'function') {
                        window.requestIdleCallback(loadStories, { timeout: 500 });
                        return;
                    }
                    loadStories();
                }, 100);
            };
            if (document.readyState === 'complete') {
                start();
                return;
            }
            window.addEventListener('load', start, { once: true });
        };

        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            observer.disconnect();
            scheduleStoriesLoad();
        }, { rootMargin: '300px 0px' });
        observer.observe($container[0]);
    });
}

/**
 * Initialize Slick slider with lazy video loading
 */
function initSlider($html) {
    const $slider = $html.find(STORIES_SLIDER_SELECTOR);
    if ($slider.length === 0) return;

    if ($slider.hasClass('stories-bubble-scroller')) {
        initNativeBubbleScroller($slider);
        return;
    }

    if (!getStoriesSlickPlugin()) return;

    const rawOpts = $slider.attr('data-gslick');
    if (!rawOpts) return;

    try {
        const opts = JSON.parse(rawOpts);
        if (!$slider.hasClass('slick-initialized')) {
            callStoriesSlick($slider, opts);
            setupVideoLoading($slider);
            loadNearbyStoryPosters($slider);

            let userNavigationPending = false;
            let userNavigationTimer = null;
            const markUserNavigation = () => {
                userNavigationPending = true;
                if (userNavigationTimer) clearTimeout(userNavigationTimer);
                userNavigationTimer = setTimeout(() => {
                    userNavigationPending = false;
                }, 5000);
            };
            const consumeUserNavigation = () => {
                const wasUserNavigation = userNavigationPending;
                userNavigationPending = false;
                if (userNavigationTimer) clearTimeout(userNavigationTimer);
                userNavigationTimer = null;
                return wasUserNavigation;
            };

            $slider.on('pointerdown touchstart mousedown', '.slick-list, .slick-arrow', markUserNavigation);
            $slider.on('keydown', '.slick-arrow', event => {
                if (['Enter', ' ', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                    markUserNavigation();
                }
            });
            $slider.on('click', '.slick-next', () => {
                markUserNavigation();
                const slick = $slider[0]?.slick;
                if (!slick) return;

                const slidesToShow = slick.options.slidesToShow || 1;
                if (slick.currentSlide >= slick.slideCount - slidesToShow - 4) {
                    loadMoreStories('horizontal-user');
                }
            });

            // Autoplay also emits afterChange. Hydrate nearby posters for every
            // change, but paginate only after explicit pointer/keyboard input.
            $slider.on('afterChange', (event, slick, currentSlide) => {
                loadNearbyStoryPosters($slider);
                trackVisibleStoryImpressions($slider);
                const totalSlides = slick.slideCount;
                const slidesToShow = slick.options.slidesToShow || 1;
                const wasUserNavigation = consumeUserNavigation();

                // If we are within 4 slides of the end, load more
                if (wasUserNavigation && currentSlide >= totalSlides - slidesToShow - 4) {
                    if (StoriesState.hasMore && !StoriesState.isLoading) {
                        loadMoreStories('horizontal-user');
                    }
                }
            });

            // If we hit the absolute edge and try to go further
            $slider.on('edge', (event, slick, direction) => {
                if (direction === 'left' && consumeUserNavigation()) { // 'left' means moving content left, i.e., going next
                    if (StoriesState.hasMore && !StoriesState.isLoading) {
                        loadMoreStories('horizontal-user');
                    }
                }
            });
        }
    } catch (err) {
        storiesDebug('slider.initialization.failed', { message: err?.message || String(err) });
        console.error('Failed to initialize stories slider:', err);
    }
}

function initNativeBubbleScroller($slider) {
    const slider = $slider[0];
    if (!slider || slider.dataset.storiesScrollerInitialized === '1') return;
    slider.dataset.storiesScrollerInitialized = '1';

    let frame = null;
    let userNavigationPending = false;
    const update = () => {
        frame = null;
        loadNearbyStoryPosters($slider);
        trackVisibleStoryImpressions($slider);
        if (userNavigationPending && slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - slider.clientWidth) {
            userNavigationPending = false;
            if (StoriesState.hasMore && !StoriesState.isLoading) loadMoreStories('horizontal-user');
        }
    };
    const scheduleUpdate = () => {
        if (frame === null) frame = requestAnimationFrame(update);
    };

    slider.addEventListener('pointerdown', () => { userNavigationPending = true; }, { passive: true });
    slider.addEventListener('touchstart', () => { userNavigationPending = true; }, { passive: true });
    slider.addEventListener('wheel', () => { userNavigationPending = true; }, { passive: true });
    slider.addEventListener('scroll', scheduleUpdate, { passive: true });
    slider.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        userNavigationPending = true;
        event.preventDefault();
        slider.scrollBy({
            left: (event.key === 'ArrowRight' ? 1 : -1) * slider.clientWidth * 0.75,
            behavior: 'smooth'
        });
    });
    update();
}

/**
 * Setup lazy video loading for slider
 */
function setupVideoLoading($slider) {
    if (StoriesState.type === 'bubbles') return;

    /**
     * Load video source for a specific story index (handles clones too)
     */
    const loadVideo = (storyIndex) => {
        // Find all instances (original + clones) for this story index
        const $storySlides = $slider.find(`.story-slide[data-story-index="${storyIndex}"]`);

        $storySlides.each(function () {
            const $slide = $(this).closest('.slick-slide');
            const $video = $slide.find('.lazy-video');

            if ($video.length > 0 && $video.data('src') && !$video.attr('src')) {

                $video.addClass('loading').attr('src', $video.data('src')).attr('preload', 'auto');
                $video[0].load();

                // Optional: Listen for load to clear loading state
                $video.one('loadeddata', () => $video.removeClass('loading'));
            }
        });
    };

    /**
     * Load videos for the range of slides starting at index
     */
    const loadVisibleVideos = (startIndex) => {
        // Optimization: Don't load if fullscreen is active
        const modal = document.getElementById('storiesFullscreenModal');
        if (modal && modal.classList.contains('active')) return;

        const slick = callStoriesSlick($slider, 'getSlick');
        if (!slick) return;
        const slidesToShow = slick.options.slidesToShow || 1;
        const total = slick.slideCount || 0;

        if (total === 0) return;

        // Load visible range + 1 buffer
        for (let i = 0; i < slidesToShow + 1; i++) {
            const idx = (startIndex + i) % total;
            loadVideo(idx);
        }
    };

    // Initial load, delayed to ensure slick is ready
    setTimeout(() => {
        const current = callStoriesSlick($slider, 'slickCurrentSlide');
        loadVisibleVideos(current || 0);
    }, 500);

    // Load on beforeChange (preload next)
    $slider.on('beforeChange', (_, __, ___, next) => {
        // Pause current playing videos to prevent background audio chaos
        $slider.find('.slick-slide.slick-active video').each(function () {
            if (!this.paused) this.pause();
        });

        loadVisibleVideos(next);
    });

    // Play on afterChange
    $slider.on('afterChange', () => {
        // Play all videos in currently active slides
        // We rely on .slick-active class which Slick updates reliably
        $slider.find('.slick-slide.slick-active .lazy-video').each(function () {
            // Ensure muted for autoplay policy
            this.muted = true;
            if (this.paused && this.readyState >= 2) {
                this.play().catch(() => { });
            }
        });
    });
}

/**
 * Initialize all fullscreen modal functions
 */
function initFullscreenFunctions() {
    storiesDebug('fullscreen.bindings.started');

    // Open fullscreen modal
    window.openStoriesFullscreen = function (storyIdOrIndex) {
        const modal = document.getElementById('storiesFullscreenModal');
        const container = document.getElementById('storiesFullscreenContainer');

        storiesDebug('fullscreen.open.requested', { requested_story_or_index: storyIdOrIndex });
        if (!modal || !container) {
            storiesDebug('fullscreen.open.skipped', {
                reason: 'missing_elements',
                modal_found: Boolean(modal),
                container_found: Boolean(container)
            });
            return;
        }

        // Pause horizontal slider to save resources
        const $slider = $('.growtype-stories-slider-container').find(STORIES_SLIDER_SELECTOR);
        if ($slider.length) {
            $slider.find('video').each(function () { this.pause(); });
            if ($slider.hasClass('slick-initialized')) {
                callStoriesSlick($slider, 'slickPause');
            }
        }

        // Use stories from PHP-injected data or state
        const stories = window.storiesData || StoriesState.stories || [];
        if (stories.length === 0) {
            storiesDebug('fullscreen.open.skipped', { reason: 'no_stories' });
            return;
        }

        // Store in state
        StoriesState.stories = stories;
        StoriesState.loadedIds = stories.map(storyIdentity).filter(Boolean);
        StoriesState.stories.forEach(story => appendStoryVariantIds(story));

        // Find the exact video first; retain character/index fallback for old markup.
        let startIndex = 0;
        if (storyIdOrIndex !== undefined) {
            const requestedId = String(storyIdOrIndex);
            let foundIndex = stories.findIndex(story => (
                storyIdentity(story) === requestedId
                || storyVariants(story).some(variant => storyIdentity(variant) === requestedId)
            ));
            if (foundIndex < 0) {
                foundIndex = stories.findIndex(story => String(story.character?.id) === requestedId);
            }
            if (foundIndex >= 0) {
                startIndex = foundIndex;
            } else if (!isNaN(parseInt(requestedId))) {
                startIndex = Math.min(parseInt(requestedId), stories.length - 1);
            }
        }
        storiesDebug('fullscreen.open.index_resolved', {
            requested_story_or_index: storyIdOrIndex,
            start_index: startIndex,
            stories_count: stories.length,
            start_story_id: storyIdentity(stories[startIndex])
        });

        // Build slides
        container.innerHTML = '';
        stories.forEach((story, idx) => {
            container.appendChild(createSlide(story, idx));
        });
        storiesDebug('fullscreen.open.slides_built', { slide_count: container.children.length });

        // Show modal
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
        StoriesState.currentIndex = startIndex;
        StoriesState.modalSessionId = createTrackingId('stories-modal');
        StoriesState.modalOpenedAt = performance.now();
        StoriesState.modalViewedIds = [];
        StoriesState.lastViewedStoryId = '';
        StoriesState.pendingNavigationMethod = 'open';
        updateStoryUrl(storyIdentity(stories[startIndex]));
        storiesDebug('fullscreen.open.activated', {
            start_index: startIndex,
            scroll_top: container.scrollTop,
            client_height: container.clientHeight
        });

        trackStoriesEvent('opened', stories[startIndex], startIndex, {
            initial_story_count: stories.length
        });

        // Show scroll hint for first-time users
        const scrollHint = document.getElementById('storiesScrollHint');
        if (scrollHint && !safeStorage.local.get('growtype_stories_hint_shown_v2')) {
            scrollHint.classList.add('active');

            let scrollTimer = null;
            const hideHint = () => {
                if (scrollTimer) clearTimeout(scrollTimer);
                dismissStoriesHint();
                container.removeEventListener('scroll', hideHint);
            };

            // Delay adding scroll listener to avoid hiding on initial programmatic scroll
            scrollTimer = setTimeout(() => {
                container.addEventListener('scroll', hideHint, { passive: true });
            }, 1000);

            // Also hide on arrow clicks
            modal.querySelectorAll('.fullscreen-nav').forEach(nav => {
                nav.addEventListener('click', hideHint, { once: true });
            });
        }

        // Create IntersectionObserver for instant play
        if (StoriesState.observer) StoriesState.observer.disconnect();

        StoriesState.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const video = entry.target.querySelector('video');
                const slide = entry.target;
                if (!video) return;

                if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                    // Immediate State Update
                    const idx = [...container.children].indexOf(slide);
                    storiesDebug('observer.slide_active', {
                        observer_index: idx,
                        intersection_ratio: entry.intersectionRatio,
                        slide_story_id: slide.dataset.storyId || '',
                        scroll_top: container.scrollTop
                    });
                    if (idx !== -1) StoriesState.currentIndex = idx;

                    if (entry.intersectionRatio >= 0.6) {
                        const storyId = slide.dataset.storyId || slide.dataset.characterId;
                        startStoryView(slide, video);
                        if (storyId && !StoriesState.modalViewedIds.includes(String(storyId))) {
                            StoriesState.modalViewedIds.push(String(storyId));
                        }

                        scheduleWatchedStory(slide);
                    }

                    // Logic to execute loading and playback
                    const triggerAction = () => {
                        storiesDebug('observer.playback.requested', {
                            slide_story_id: slide.dataset.storyId || '',
                            video_ready_state: video.readyState,
                            video_src: video.currentSrc || video.dataset.src || ''
                        });
                        preloadFullscreenVideo(video);
                        video.muted = StoriesState.isMuted;
                        // Ensure we don't error on play
                        video.play().catch(error => {
                            storiesDebug('observer.playback.failed', {
                                slide_story_id: slide.dataset.storyId || '',
                                message: error?.message || String(error)
                            });
                        });

                        // Preload neighbors
                        preloadNearbyFullscreenVideos(container, StoriesState.currentIndex);

                        // Seamless Infinite Scroll
                        if (container.children.length - StoriesState.currentIndex <= 4) {
                            loadMoreStories();
                        }
                    };

                    // IntersectionObserver's 60% threshold already identifies
                    // one active slide. Start it immediately instead of adding
                    // 100 ms of artificial first-frame latency.
                    if (slide.loadTimer) clearTimeout(slide.loadTimer);
                    triggerAction();

                } else {
                    if (entry.intersectionRatio > 0 || StoriesState.activeView?.slide === slide) {
                        storiesDebug('observer.slide_inactive', {
                            slide_story_id: slide.dataset.storyId || '',
                            intersection_ratio: entry.intersectionRatio,
                            scroll_top: container.scrollTop
                        });
                    }
                    // Slide is inactive
                    if (slide.loadTimer) {
                        clearTimeout(slide.loadTimer);
                        slide.loadTimer = null;
                    }
                    video.pause();
                    const storyId = slide.dataset.storyId || slide.dataset.characterId;
                    clearStoryWatchTimer(storyId);
                    if (StoriesState.activeView?.slide === slide) {
                        endActiveStoryView('story_changed');
                    }
                }
            });
        }, {
            root: container,
            threshold: 0.6 // Play when 60% visible
        });

        // Observe existing slides
        container.querySelectorAll('.fullscreen-story:not(.fullscreen-loading)').forEach(slide => {
            StoriesState.observer.observe(slide);
        });
        storiesDebug('observer.bound', {
            observed_slide_count: container.querySelectorAll('.fullscreen-story:not(.fullscreen-loading)').length
        });

        // Start the requested story synchronously. Waiting for a delayed
        // scroll plus IntersectionObserver can leave the first video without
        // a src, especially while the modal is being laid out.
        const initialSlide = container.children[startIndex];
        const initialVideo = initialSlide?.querySelector('video');
        if (initialSlide && initialVideo) {
            container.scrollTop = initialSlide.offsetTop;
            preloadFullscreenVideo(initialVideo);
            initialVideo.muted = StoriesState.isMuted;
            initialVideo.play().catch(error => {
                storiesDebug('fullscreen.open.initial_video_failed', {
                    start_index: startIndex,
                    message: error?.message || String(error)
                });
            });
            preloadNearbyFullscreenVideos(container, startIndex);
            storiesDebug('fullscreen.open.initial_video_started', {
                start_index: startIndex,
                slide_story_id: initialSlide.dataset.storyId || '',
                scroll_top: container.scrollTop,
                video_ready_state: initialVideo.readyState
            });
        }

        // Cleanup on close
        // (Handled by disconnect in window.closeStoriesFullscreen or next open)
    };

    // Close fullscreen
    window.closeStoriesFullscreen = function (exitReason = 'close_button') {
        const modal = document.getElementById('storiesFullscreenModal');
        storiesDebug('fullscreen.close.requested', {
            exit_reason: exitReason,
            is_active: Boolean(modal?.classList.contains('active'))
        });
        if (modal?.classList.contains('active')) {
            endActiveStoryView('modal_closed');
            const modalDuration = StoriesState.modalOpenedAt
                ? Math.round((performance.now() - StoriesState.modalOpenedAt) / 10) / 100
                : 0;
            trackStoriesEvent('closed', null, null, {
                modal_duration_seconds: modalDuration,
                unique_stories_viewed: StoriesState.modalViewedIds.length,
                exit_reason: exitReason
            });
            modal.classList.remove('active');
            document.body.style.overflow = '';
            modal.querySelectorAll('video').forEach(v => v.pause());

            // Ensure hint is marked as shown on close
            safeStorage.local.set('growtype_stories_hint_shown_v2', 'true');
            const scrollHint = document.getElementById('storiesScrollHint');
            if (scrollHint) scrollHint.classList.remove('active');

            // Sync horizontal slider position
            const container = document.getElementById('storiesFullscreenContainer');
            const activeSlide = container?.children[StoriesState.currentIndex];
            if (activeSlide) {
                const storyId = activeSlide.dataset.storyId || activeSlide.dataset.characterId;
                syncHorizontalSlider(storyId);
            }

            // Resume playback and autoplay on horizontal slider
            const $slider = $('.growtype-stories-slider-container').find(STORIES_SLIDER_SELECTOR);
            if ($slider.length && $slider.hasClass('slick-initialized')) {
                callStoriesSlick($slider, 'slickPlay');

                setTimeout(() => {
                    $slider.find('.slick-current video, .slick-active video').each(function () {
                        this.play().catch(() => { });
                    });
                }, 500);
            }

            StoriesState.modalSessionId = '';
            StoriesState.modalOpenedAt = 0;
            StoriesState.modalViewedIds = [];
            clearStoryUrl();
            storiesDebug('fullscreen.closed', { exit_reason: exitReason });
        }
    };

    function syncVisibleStoryIndex(container, slides) {
        const previousIndex = StoriesState.currentIndex;
        let nearestIndex = previousIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetTop - container.scrollTop);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = index;
            }
        });

        StoriesState.currentIndex = nearestIndex;
        storiesDebug('navigation.index_synchronized', {
            previous_index: previousIndex,
            nearest_index: nearestIndex,
            nearest_distance: nearestDistance,
            scroll_top: container.scrollTop,
            slide_count: slides.length
        });
    }

    // Navigate to next story
    window.scrollStoriesNext = function (navigationMethod = 'arrow_next') {
        const container = document.getElementById('storiesFullscreenContainer');
        storiesDebug('navigation.next.requested', { navigation_method: navigationMethod });
        if (!container) {
            storiesDebug('navigation.next.skipped', { reason: 'missing_container' });
            return;
        }

        const slides = container.querySelectorAll('.fullscreen-story:not(.fullscreen-loading)');
        syncVisibleStoryIndex(container, slides);


        // Preload if we are close to the end (e.g. 3 slides remaining)
        if (slides.length - StoriesState.currentIndex <= 3) {

            loadMoreStories();
        }

        if (StoriesState.currentIndex < slides.length - 1) {
            // Go to next existing slide

            dismissStoriesHint();
            StoriesState.pendingNavigationMethod = navigationMethod;
            const previousIndex = StoriesState.currentIndex;
            StoriesState.currentIndex++;
            storiesDebug('navigation.next.accepted', {
                navigation_method: navigationMethod,
                previous_index: previousIndex,
                next_index: StoriesState.currentIndex,
                next_story_id: slides[StoriesState.currentIndex]?.dataset.storyId || ''
            });
            navigateToSlide(slides, StoriesState.currentIndex);
        } else {
            storiesDebug('navigation.next.boundary', {
                current_index: StoriesState.currentIndex,
                slide_count: slides.length,
                is_loading: StoriesState.isLoading,
                has_more: StoriesState.hasMore
            });
            // At the end. If not loading, try loading (though preloading check should have caught it)

            if (!StoriesState.isLoading && StoriesState.hasMore) {
                loadMoreStories();
            }
        }
    };

    // Navigate to previous story
    window.scrollStoriesPrev = function (navigationMethod = 'arrow_previous') {
        const container = document.getElementById('storiesFullscreenContainer');
        storiesDebug('navigation.previous.requested', { navigation_method: navigationMethod });
        if (!container) {
            storiesDebug('navigation.previous.skipped', { reason: 'missing_container' });
            return;
        }

        const slides = container.querySelectorAll('.fullscreen-story:not(.fullscreen-loading)');
        syncVisibleStoryIndex(container, slides);

        if (StoriesState.currentIndex > 0) {
            dismissStoriesHint();
            StoriesState.pendingNavigationMethod = navigationMethod;
            const previousIndex = StoriesState.currentIndex;
            StoriesState.currentIndex--;
            storiesDebug('navigation.previous.accepted', {
                navigation_method: navigationMethod,
                previous_index: previousIndex,
                next_index: StoriesState.currentIndex,
                next_story_id: slides[StoriesState.currentIndex]?.dataset.storyId || ''
            });
            navigateToSlide(slides, StoriesState.currentIndex);
        } else {
            storiesDebug('navigation.previous.boundary', { current_index: StoriesState.currentIndex });
        }
    };

    // Change only the media item inside the currently visible character.
    window.changeCurrentCharacterStory = async function (direction, navigationMethod = 'horizontal_story') {
        const container = document.getElementById('storiesFullscreenContainer');
        storiesDebug('horizontal.requested', { direction, navigation_method: navigationMethod });
        if (!container) {
            storiesDebug('horizontal.skipped', { reason: 'missing_container' });
            return;
        }

        const slides = container.querySelectorAll('.fullscreen-story:not(.fullscreen-loading)');
        const slide = slides[StoriesState.currentIndex];
        const video = slide?.querySelector('video');
        if (!slide || !video) {
            storiesDebug('horizontal.skipped', { reason: 'missing_slide_or_video' });
            return;
        }

        const group = storyGroupByIdentity(slide.dataset.storyId);
        const variants = storyVariants(group);
        if (!group || variants.length < 2) {
            storiesDebug('horizontal.skipped', {
                reason: 'insufficient_variants',
                variant_count: variants.length,
                slide_story_id: slide.dataset.storyId || ''
            });
            return;
        }

        const currentIndex = Math.max(0, variants.findIndex(variant => (
            storyIdentity(variant) === String(slide.dataset.storyId || '')
        )));
        const nextIndex = (currentIndex + direction + variants.length) % variants.length;
        const nextStory = storyFromVariant(group, variants[nextIndex]);
        if (!nextStory || storyIdentity(nextStory) === String(slide.dataset.storyId || '')) {
            storiesDebug('horizontal.skipped', { reason: 'same_or_missing_story' });
            return;
        }

        const nextStoryId = storyIdentity(nextStory);
        const transitionId = `${nextStoryId}-${Date.now()}`;
        slide.dataset.storyTransitionId = transitionId;
        storiesDebug('horizontal.transition_started', {
            previous_story_id: slide.dataset.storyId || '',
            next_story_id: nextStoryId,
            current_variant_index: currentIndex,
            next_variant_index: nextIndex,
            transition_id: transitionId
        });

        const loader = showStoryTransitionSpinner(slide);

        // Let the spinner paint over the outgoing video before swapping the
        // media source. No character/poster image is shown during this handoff.
        await waitForStoryTransitionSpinner();
        if (slide.dataset.storyTransitionId !== transitionId) {
            storiesDebug('horizontal.transition_cancelled', { transition_id: transitionId });
            return;
        }

        dismissStoriesHint();
        endActiveStoryView('story_changed');
        video.pause();
        video.removeAttribute('src');
        video.dataset.src = nextStory.video?.url || '';
        slide.dataset.storyId = nextStoryId;
        delete slide.dataset.storyTransitionId;
        loader?.classList.add('visible');

        StoriesState.pendingNavigationMethod = navigationMethod;
        startStoryView(slide, video);
        preloadFullscreenVideo(video);
        video.muted = StoriesState.isMuted;
        video.play().catch(() => { });
        syncHorizontalSlider(slide.dataset.characterId);
        storiesDebug('horizontal.transition_completed', {
            next_story_id: nextStoryId,
            video_ready_state: video.readyState
        });
    };

    const gestureContainer = document.getElementById('storiesFullscreenContainer');
    let gestureStart = null;
    gestureContainer?.addEventListener('touchstart', event => {
        const touch = event.touches[0];
        gestureStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
        storiesDebug('touch.start', { gesture_start: gestureStart });
    }, { passive: true });
    gestureContainer?.addEventListener('touchmove', event => {
        if (!gestureStart) return;
        const touch = event.touches[0];
        if (!touch) return;

        const deltaX = touch.clientX - gestureStart.x;
        const deltaY = touch.clientY - gestureStart.y;
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
            event.preventDefault();
            storiesDebug('touch.move.prevented', { delta_x: deltaX, delta_y: deltaY });
        }
    }, { passive: false });
    gestureContainer?.addEventListener('touchend', event => {
        if (!gestureStart) return;
        const touch = event.changedTouches[0];
        if (!touch) return;

        const deltaX = touch.clientX - gestureStart.x;
        const deltaY = touch.clientY - gestureStart.y;
        gestureStart = null;
        storiesDebug('touch.end', { delta_x: deltaX, delta_y: deltaY });

        if (Math.abs(deltaY) >= 50 && Math.abs(deltaY) > Math.abs(deltaX)) {
            if (deltaY < 0) {
                storiesDebug('touch.vertical.accepted', { direction: 'next' });
                window.scrollStoriesNext('swipe_up_next_character');
            } else {
                storiesDebug('touch.vertical.accepted', { direction: 'previous' });
                window.scrollStoriesPrev('swipe_down_previous_character');
            }
            return;
        }

        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) {
            storiesDebug('touch.skipped', { reason: 'below_horizontal_threshold' });
            return;
        }

        if (deltaX > 0) {
            storiesDebug('touch.horizontal.accepted', { direction: 'previous' });
            window.changeCurrentCharacterStory(-1, 'swipe_right');
        } else {
            storiesDebug('touch.horizontal.accepted', { direction: 'next' });
            window.changeCurrentCharacterStory(1, 'swipe_left');
        }
    }, { passive: true });

    // macOS trackpads report two-finger swipes as wheel event bursts. Keep one
    // owner for each axis: native scroll snap moves between characters, while
    // JavaScript handles horizontal story variants.
    let trackpadGestureLocked = false;
    let trackpadGestureTimer = null;
    gestureContainer?.addEventListener('wheel', event => {
        const modal = document.getElementById('storiesFullscreenModal');
        if (!modal?.classList.contains('active')) {
            storiesDebug('wheel.skipped', { reason: 'modal_inactive' });
            return;
        }

        const isVerticalAxis = Math.abs(event.deltaY) > Math.abs(event.deltaX);
        const isVertical = Math.abs(event.deltaY) >= 8 && isVerticalAxis;
        storiesDebug('wheel.received', {
            delta_x: event.deltaX,
            delta_y: event.deltaY,
            delta_mode: event.deltaMode,
            cancelable: event.cancelable,
            default_prevented: event.defaultPrevented,
            classified_vertical: isVertical,
            horizontal_locked: trackpadGestureLocked,
            scroll_top: gestureContainer.scrollTop
        });
        if (isVerticalAxis) {
            storiesDebug('wheel.vertical.native_scroll', {
                default_prevented: event.defaultPrevented,
                cancelable: event.cancelable,
                delta_y: event.deltaY,
                scroll_top: gestureContainer.scrollTop
            });
            if (!isVertical) {
                storiesDebug('wheel.vertical.ignored', { reason: 'below_threshold' });
                return;
            }
            dismissStoriesHint();
            return;
        }

        const isHorizontalAxis = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        const isHorizontal = Math.abs(event.deltaX) >= 8 && isHorizontalAxis;
        if (isHorizontalAxis) event.preventDefault();
        if (!isHorizontal) {
            storiesDebug('wheel.skipped', { reason: 'below_axis_threshold' });
            return;
        }

        event.preventDefault();
        if (trackpadGestureTimer) clearTimeout(trackpadGestureTimer);
        trackpadGestureTimer = setTimeout(() => {
            trackpadGestureLocked = false;
            storiesDebug('wheel.horizontal.unlocked');
        }, 350);
        if (trackpadGestureLocked) {
            storiesDebug('wheel.horizontal.ignored', { reason: 'momentum_lock' });
            return;
        }

        trackpadGestureLocked = true;
        if (event.deltaX > 0) {
            storiesDebug('wheel.horizontal.accepted', { direction: 'next' });
            window.changeCurrentCharacterStory(1, 'trackpad_swipe_left');
        } else {
            storiesDebug('wheel.horizontal.accepted', { direction: 'previous' });
            window.changeCurrentCharacterStory(-1, 'trackpad_swipe_right');
        }
    }, { passive: false });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        const modal = document.getElementById('storiesFullscreenModal');
        if (!modal?.classList.contains('active')) return;

        storiesDebug('keyboard.received', { key: e.key });

        switch (e.key) {
            case 'Escape': window.closeStoriesFullscreen('escape_key'); break;
            case 'ArrowDown': window.scrollStoriesNext('keyboard_next_character'); break;
            case 'ArrowUp': window.scrollStoriesPrev('keyboard_previous_character'); break;
            case 'ArrowRight': window.changeCurrentCharacterStory(1, 'keyboard_next_story'); break;
            case 'ArrowLeft': window.changeCurrentCharacterStory(-1, 'keyboard_previous_story'); break;
        }
    });

    if (!StoriesState.lifecycleTrackingInitialized) {
        StoriesState.lifecycleTrackingInitialized = true;

        document.addEventListener('visibilitychange', () => {
            const modal = document.getElementById('storiesFullscreenModal');
            if (!modal?.classList.contains('active')) return;

            if (document.visibilityState === 'hidden') {
                endActiveStoryView('page_hidden');
                modal.querySelectorAll('video').forEach(video => video.pause());
                return;
            }

            const container = document.getElementById('storiesFullscreenContainer');
            const slide = container?.children[StoriesState.currentIndex];
            const video = slide?.querySelector('video');
            if (slide && video) {
                StoriesState.pendingNavigationMethod = 'page_visible';
                startStoryView(slide, video);
                video.muted = StoriesState.isMuted;
                video.play().catch(() => { });
            }
        });

        window.addEventListener('pagehide', () => endActiveStoryView('page_exit'));
    }
}

/**
 * Navigate to a specific slide
 */
function navigateToSlide(slides, index) {
    const targetSlide = slides[index];
    const targetVideo = targetSlide?.querySelector('video');
    const container = document.getElementById('storiesFullscreenContainer');
    storiesDebug('transition.requested', {
        target_index: index,
        target_story_id: targetSlide?.dataset.storyId || '',
        target_offset_top: targetSlide?.offsetTop ?? null,
        current_scroll_top: container?.scrollTop ?? null,
        target_video_found: Boolean(targetVideo)
    });
    const playTargetVideo = () => {
        if (!targetVideo) {
            storiesDebug('transition.video_play.skipped', { reason: 'missing_target_video', target_index: index });
            return;
        }
        targetVideo.muted = StoriesState.isMuted;
        storiesDebug('transition.video_play.requested', {
            target_index: index,
            target_story_id: targetSlide?.dataset.storyId || '',
            ready_state: targetVideo.readyState
        });
        targetVideo.play().then(() => {
            storiesDebug('transition.video_play.started', {
                target_index: index,
                target_story_id: targetSlide?.dataset.storyId || ''
            });
        }).catch(error => {
            storiesDebug('transition.video_play.failed', {
                target_index: index,
                message: error?.message || String(error)
            });
        });
    };

    // Show only the spinner while the incoming character video starts.
    showStoryTransitionSpinner(targetSlide);

    // 1. Preload Target IMMEDIATELY
    if (targetVideo) {
        storiesDebug('transition.video_preload.requested', {
            target_index: index,
            data_src: targetVideo.dataset.src || '',
            current_src: targetVideo.currentSrc || targetVideo.src || ''
        });
        preloadFullscreenVideo(targetVideo);
    }

    // 2. Pause others (skip target)
    slides.forEach((s, i) => {
        if (i === index) return;
        const v = s.querySelector('video');
        if (v) v.pause();
    });
    storiesDebug('transition.other_videos_paused', { target_index: index });

    // 3. Glide one full-screen character into place without fading.
    if (container && targetSlide) {
        smoothScrollToStory(container, targetSlide.offsetTop, playTargetVideo);
    } else {
        playTargetVideo();
    }

    // 4. Explicit preload call for neighbors
    if (container) preloadNearbyFullscreenVideos(container, index);
    storiesDebug('transition.neighbors_preloaded', { target_index: index });

    // 5. Sync horizontal slider
    const storyId = slides[index]?.dataset.storyId || slides[index]?.dataset.characterId;
    syncHorizontalSlider(storyId);
    storiesDebug('transition.horizontal_slider_synced', { target_index: index, target_story_id: storyId || '' });

}

function smoothScrollToStory(container, targetY, onComplete = null) {
    storiesDebug('scroll.transition.started', {
        start_y: container.scrollTop,
        target_y: targetY,
        distance: targetY - container.scrollTop,
        previous_scroll_snap_type: container.style.scrollSnapType || ''
    });
    if (Math.abs(container.scrollTop - targetY) < 1) {
        container.scrollTop = targetY;
        storiesDebug('scroll.transition.completed', { reason: 'already_at_target', target_y: targetY });
        onComplete?.();
        return;
    }

    const previousScrollSnapType = container.style.scrollSnapType;
    let completed = false;
    let settleTimer = null;
    let fallbackTimer = null;

    const finish = (reason) => {
        if (completed) {
            storiesDebug('scroll.transition.finish_skipped', { reason: 'already_completed', attempted_reason: reason });
            return;
        }
        completed = true;
        if (settleTimer) clearTimeout(settleTimer);
        if (fallbackTimer) clearTimeout(fallbackTimer);
        container.removeEventListener('scroll', handleScroll);
        container.scrollTop = targetY;
        container.style.scrollSnapType = previousScrollSnapType;
        storiesDebug('scroll.transition.completed', {
            reason,
            target_y: targetY,
            final_scroll_top: container.scrollTop,
            restored_scroll_snap_type: previousScrollSnapType || ''
        });
        onComplete?.();
    };
    const handleScroll = () => {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
            const remainingDistance = Math.abs(container.scrollTop - targetY);
            storiesDebug('scroll.transition.settle_checked', {
                target_y: targetY,
                scroll_top: container.scrollTop,
                remaining_distance: remainingDistance
            });
            if (remainingDistance < 2) finish('scroll_settled');
        }, 60);
    };

    // Native smooth scrolling is compositor-driven and matches the full-card
    // vertical motion used by Stories/Reels interfaces.
    container.style.scrollSnapType = 'none';
    container.addEventListener('scroll', handleScroll, { passive: true });
    container.scrollTo({ top: targetY, behavior: 'smooth' });
    storiesDebug('scroll.transition.native_scroll_requested', { target_y: targetY });
    fallbackTimer = setTimeout(() => finish('fallback_timeout'), 700);
}

/**
 * Create fullscreen slide HTML
 */
function createSlide(story, index) {
    const slide = document.createElement('div');
    slide.className = 'fullscreen-story';
    slide.dataset.storyId = storyIdentity(story);
    slide.dataset.characterId = story.character?.id || story.character?.external_id || '';
    slide.dataset.externalId = story.character?.external_id || '';
    storiesDebug('slide.created', {
        slide_index: index,
        slide_story_id: slide.dataset.storyId,
        character_id: slide.dataset.characterId
    });

    const isLocked = story.character?.is_locked;
    const profileUrl = story.character?.profile_url || '#';
    const profileCoverUrl = story.character?.cover_image_url || '';
    const chatUrl = isLocked ? 'javascript:void(0);' : (story.character?.chat_url || '#');

    const lockHtml = isLocked ? `
        <div class="locked-overlay" onclick="event.stopPropagation(); window.unlockStory('${story.character.id}', '${storyIdentity(story)}', event)">
            <svg class="locked-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span class="locked-text">Locked</span>
        </div>` : '';

    slide.innerHTML = `
        <video 
            class="fullscreen-video" 
            data-src="${story.video?.url || ''}"
            loop playsinline muted preload="auto"
            data-index="${index}"
        ></video>
        <div class="fullscreen-slide-loader visible"></div>
        <div class="fullscreen-overlay"></div>
        ${lockHtml}
        
        <div class="fullscreen-actions">
            <button class="action-btn share-btn" style="display: none;" onclick="event.stopPropagation(); window.shareStory('${story.character.title}', '${profileUrl}', '${storyIdentity(story)}', event)">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16 6 12 2 8 6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                </svg>
                <span>Share</span>
            </button>
            
            <button class="action-btn like-btn" onclick="window.handleStoryLike('${story.character.id}', event)">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                <span>Like</span>
            </button>

            <button class="action-btn dislike-btn" onclick="window.handleStoryDislike('${story.character.id}', event)">
                <i class="fa-regular fa-thumbs-down" style="font-size: 24px;"></i>
                <span>Dislike</span>
            </button>
            
            <a href="${chatUrl}" class="action-btn chat-btn ${isLocked ? 'disabled' : ''}" data-story-action="chat" style="color:#ff9000;" onclick="event.stopPropagation();">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>Chat</span>
            </a>

            <a href="${profileUrl}" class="action-btn profile-btn" data-story-action="profile" onclick="event.stopPropagation();">
                ${profileCoverUrl ? `
                    <img src="${profileCoverUrl}" alt="${story.character?.title || ''}" class="profile-cover-image" loading="lazy" decoding="async">
                ` : `
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                `}
                <span>Profile</span>
            </a>
        </div>
        
        <div class="audio-toggle ${StoriesState.isMuted ? '' : 'unmuted'}" onclick="window.toggleStoryAudio('${story.character.id}', event)">
            <svg class="mute-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                <line x1="23" y1="1" x2="1" y2="23" stroke="currentColor" stroke-width="2"></line>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" opacity="0.3"></path>
            </svg>
            <svg class="unmute-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
            </svg>
        </div>
        
        <div class="fullscreen-info">

        </div>
        
        <div class="fullscreen-nav prev" onclick="event.stopPropagation(); window.changeCurrentCharacterStory(-1, 'arrow_previous_story');">‹</div>
        <div class="fullscreen-nav next" onclick="event.stopPropagation(); window.changeCurrentCharacterStory(1, 'arrow_next_story');">›</div>
    `;

    // Handle loader visibility
    const video = slide.querySelector('video');
    const loader = slide.querySelector('.fullscreen-slide-loader');

    if (video) {
        const hideLoader = () => {
            if (loader) loader.classList.remove('visible');
        };
        const revealVideo = () => {
            video.classList.add('is-ready');
            hideLoader();
            if (loader) loader.classList.remove('story-transition-loading');
        };
        const showLoader = () => {
            video.classList.remove('is-ready');
            if (loader) loader.classList.add('visible');
        };

        video.addEventListener('canplay', () => {
            storiesDebug('video.canplay', {
                slide_index: index,
                slide_story_id: slide.dataset.storyId || '',
                ready_state: video.readyState
            });
        });
        video.addEventListener('playing', () => {
            storiesDebug('video.playing', {
                slide_index: index,
                slide_story_id: slide.dataset.storyId || '',
                current_time: video.currentTime,
                ready_state: video.readyState
            });
            revealVideo();
            if (StoriesState.activeView?.video === video) {
                resumeStoryWatch(StoriesState.activeView.watch, performance.now());
                scheduleWatchedStory(slide);
            }
        });
        video.addEventListener('waiting', () => {
            storiesDebug('video.waiting', {
                slide_index: index,
                slide_story_id: slide.dataset.storyId || '',
                current_time: video.currentTime,
                ready_state: video.readyState
            });
            showLoader();
        });
        video.addEventListener('stalled', () => {
            storiesDebug('video.stalled', {
                slide_index: index,
                slide_story_id: slide.dataset.storyId || '',
                current_time: video.currentTime,
                ready_state: video.readyState
            });
            showLoader();
        });
        video.addEventListener('pause', () => {
            storiesDebug('video.paused', {
                slide_index: index,
                slide_story_id: slide.dataset.storyId || '',
                current_time: video.currentTime,
                ended: video.ended
            });
            if (StoriesState.activeView?.video === video) {
                pauseStoryWatch(StoriesState.activeView.watch, performance.now());
                clearStoryWatchTimer(StoriesState.activeView.storyId);
            }
        });
        video.addEventListener('timeupdate', () => trackStoryProgress(video));
        video.addEventListener('ended', () => {
            storiesDebug('video.ended', {
                slide_index: index,
                slide_story_id: slide.dataset.storyId || '',
                duration: video.duration
            });
            trackStoryProgress(video);
        });

        // Click handling: distinguish single vs double click
        let clickTimer = null;
        video.addEventListener('click', (e) => {
            if (clickTimer) {
                // Second click in 300ms -> Double Click (Like)
                clearTimeout(clickTimer);
                clickTimer = null;
                const btn = slide.querySelector('.like-btn');
                window.handleStoryLike(story.character.id, { currentTarget: btn, stopPropagation: () => { } });
            } else {
                // First click -> Potential Single Click (Audio)
                clickTimer = setTimeout(() => {
                    clickTimer = null;
                    window.toggleStoryAudio(story.character.id, e);
                }, 300);
            }
        });
    }

    const chatLink = slide.querySelector('[data-story-action="chat"]');
    if (chatLink && !isLocked) {
        chatLink.addEventListener('click', event => {
            const currentStory = storyContextFromElement(event.currentTarget).story || story;
            trackStoriesEvent('chat_clicked', currentStory, index, {
                story_view_id: StoriesState.activeView?.viewId || ''
            });
            setStoriesChatAttribution(currentStory, index);
            endActiveStoryView('chat_clicked');
        });
    }

    const profileLink = slide.querySelector('[data-story-action="profile"]');
    if (profileLink) {
        profileLink.addEventListener('click', event => {
            const currentStory = storyContextFromElement(event.currentTarget).story || story;
            trackStoriesEvent('profile_clicked', currentStory, index, {
                story_view_id: StoriesState.activeView?.viewId || ''
            });
            endActiveStoryView('profile_clicked');
        });
    }

    if (StoriesState.observer) {
        StoriesState.observer.observe(slide);
    }

    return slide;
}

/**
 * Create horizontal slide HTML (to match Blade partial)
 */
function createHorizontalSlideHtml(story, index) {
    const isLocked = story.character?.is_locked;
    const charId = story.character?.id || '';
    const storyId = storyIdentity(story);
    const title = story.character?.title || '';
    const titleAttribute = String(title).replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    })[character]);
    const titleInitial = (Array.from(String(title).trim())[0] || '?').replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    })[character]);
    const chatUrl = story.character?.chat_url || '#';
    const videoUrl = story.video?.url || '';
    const posterSources = story.poster_sources || {};
    const posterUrl = posterSources.src || story.poster || '';
    const posterSrcset = posterSources.srcset || '';
    const posterSizes = posterSources.sizes || '105px';
    const posterFallbackUrl = Object.prototype.hasOwnProperty.call(posterSources, 'fallback')
        ? posterSources.fallback
        : (story.poster || '');
    const likesCount = story.likes_count || 0;

    return `
        <div class="story-slide" data-story-index="${index}" data-story-id="${storyId}" data-character-id="${charId}">
            <div class="story-card" onclick="if(typeof window.openStoriesFullscreen==='function'){window.openStoriesFullscreen('${storyId}');}" style="cursor:pointer;">
                <div class="story-video-container">
                    ${(StoriesState.type === 'bubbles' && posterUrl) ? `
                        <span class="story-poster-fallback" aria-hidden="true">${titleInitial}</span>
                        <img 
                            data-src="${posterUrl}"
                            ${posterSrcset ? `data-srcset="${posterSrcset}"` : ''}
                            data-sizes="${posterSizes}"
                            data-fallback-src="${posterFallbackUrl}"
                            data-default-src="${posterSources.default || ''}"
                            alt="${titleAttribute}"
                            class="story-video"
                            loading="lazy"
                            decoding="async"
                            width="105"
                            height="105"
                            style="pointer-events: none;"
                        />
                    ` : (videoUrl ? `
                        <video 
                            data-src="${videoUrl}" 
                            muted 
                            loop 
                            playsinline 
                            class="story-video lazy-video"
                            style="pointer-events: none;"
                        ></video>
                        <div class="video-loader"></div>
                    ` : `
                        <div class="story-image-placeholder" style="background-image: url('${posterUrl}'); pointer-events: none;"></div>
                    `)}
                    
                    <div class="story-overlay" style="pointer-events: none;">
                        <div class="story-top">
                            <div class="story-live-badge">
                                <span class="live-dot"></span> LIVE
                            </div>
                            ${story.is_liked ? `
                            <div class="story-liked-indicator" style="margin-left: auto; font-size: 14px; filter: drop-shadow(0 0 5px rgba(255,59,92,0.5));">❤️</div>
                            ` : ''}
                        </div>
                        
                        ${isLocked ? `
                        <div class="locked-icon-overlay" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: rgba(255,255,255,0.8);">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        ` : ''}
                        
                        <div class="story-content" style="pointer-events: auto;">
                            ${StoriesState.type !== 'bubbles' ? `
                                <div class="story-character-info">
                                    <span class="story-name">${title}</span>
                                </div>
                            ` : ''}
                            <div class="story-actions d-none">
                                ${isLocked ? `
                                    <button class="story-chat-btn disabled" onclick="event.stopPropagation(); window.unlockStory('${charId}', '${storyId}', event)" style="border:none; cursor:pointer; background: #555; opacity: 0.8 !important;">
                                        Locked 🔒
                                    </button>
                                ` : `
                                    <a href="${chatUrl}" class="story-chat-btn" onclick="event.stopPropagation();">
                                        Chat 💬
                                    </a>
                                `}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>`;
}

/**
 * Helper: Sync horizontal slider with the exact story/video ID.
 */
function syncHorizontalSlider(storyId) {
    if (!storyId) return;
    const $slider = $('.growtype-stories-slider-container').find(STORIES_SLIDER_SELECTOR);
    if ($slider.length && $slider.hasClass('slick-initialized')) {
        let $target = $slider.find(`.slick-slide:not(.slick-cloned)[data-story-id="${storyId}"]`).first();
        if (!$target.length) {
            $target = $slider.find(`.slick-slide:not(.slick-cloned)[data-character-id="${storyId}"]`).first();
        }
        if ($target.length) {
            const idx = $target.data('slick-index');
            callStoriesSlick($slider, 'slickGoTo', idx, true); // true for instant
        }
    } else if ($slider.hasClass('stories-bubble-scroller')) {
        let $target = $slider.find(`.story-slide[data-story-id="${storyId}"]`).first();
        if (!$target.length) {
            $target = $slider.find(`.story-slide[data-character-id="${storyId}"]`).first();
        }
        $target[0]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
}

export { storiesSlider };

/**
 * Unlock story handler
 */
window.unlockStory = function (charId, storyId = '', event = null) {
    const context = storyContextFromElement(event?.currentTarget, charId);
    const story = context.story || storyByIdentity(storyId);
    trackStoriesEvent('unlock_clicked', story, context.index);
    storiesDebug('unlock.requested', { character_id: String(charId || ''), story_id: storyIdentity(story) });
    if (typeof growtype_wc_open_subscription_modal === 'function') {
        growtype_wc_open_subscription_modal();
    } else {
        alert('This character is locked. Upgrade to Premium to unlock!');
    }
};

/**
 * Share story handler
 */
window.shareStory = function (title, url, storyId = '', event = null) {
    const context = storyContextFromElement(event?.currentTarget);
    const story = context.story || storyByIdentity(storyId);
    trackStoriesEvent('share_clicked', story, context.index, {
        story_view_id: StoriesState.activeView?.viewId || ''
    });
    const siteName = document.title.split('|')[0].split('-')[0].trim() || 'AI Companion';
    const feedbackTitle = title ? title : siteName;
    const shareData = {
        title: feedbackTitle,
        text: `Check out ${feedbackTitle} on ${siteName}!`,
        url: url
    };

    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.style.cssText = `
            position: fixed;
            bottom: 100px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            color: #fff;
            padding: 12px 24px;
            border-radius: 50px;
            font-size: 14px;
            font-weight: 600;
            z-index: 10001;
            box-shadow: 0 10px 30px rgba(0,0,0,0.5);
            border: 1px solid rgba(255,255,255,0.2);
            transition: opacity 0.3s, transform 0.3s;
            pointer-events: none;
            opacity: 0;
            transform: translate(-50%, 20px);
        `;
        toast.textContent = message;
        document.body.appendChild(toast);

        // Animate in
        requestAnimationFrame(() => {
            toast.style.opacity = '1';
            toast.style.transform = 'translate(-50%, 0)';
        });

        // Remove after 3s
        setTimeout(() => {
            toast.style.opacity = '0';
            toast.style.transform = 'translate(-50%, -20px)';
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    };

    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        navigator.share(shareData)
            .then(() => storiesDebug('share.completed', { method: 'native_share', url }))
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    storiesDebug('share.failed', {
                        method: 'native_share',
                        message: error?.message || String(error)
                    });
                }
            });
    } else {
        // Fallback: Copy to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                storiesDebug('share.completed', { method: 'clipboard', url });
                showToast('Link copied to clipboard! 📋');
            }).catch(err => {
                storiesDebug('share.failed', { method: 'clipboard', message: err?.message || String(err) });
                console.error('Could not copy text: ', err);
            });
        } else {
            // Very old browsers fallback
            const textArea = document.createElement("textarea");
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                storiesDebug('share.completed', { method: 'legacy_clipboard', url });
                showToast('Link copied to clipboard! 📋');
            } catch (err) {
                storiesDebug('share.failed', { method: 'legacy_clipboard', message: err?.message || String(err) });
                console.error('Fallback copy failed', err);
            }
            document.body.removeChild(textArea);
        }
    }
};

/**
 * Helper: Load video if not loaded
 */
function preloadFullscreenVideo(video, preload = 'auto') {
    if (video.dataset.src && !video.getAttribute('src')) {
        storiesDebug('video.preload.source_attached', {
            slide_story_id: video.closest('.fullscreen-story')?.dataset.storyId || '',
            preload,
            data_src: video.dataset.src
        });
        video.setAttribute('src', video.dataset.src);
        video.preload = preload;
        video.load();
    } else if (preload === 'auto' && video.preload !== 'auto') {
        // Upgrade a metadata-warmed neighbor when it becomes active.
        storiesDebug('video.preload.upgraded', {
            slide_story_id: video.closest('.fullscreen-story')?.dataset.storyId || '',
            previous_preload: video.preload
        });
        video.preload = 'auto';
        video.load();
    } else {
        storiesDebug('video.preload.skipped', {
            slide_story_id: video.closest('.fullscreen-story')?.dataset.storyId || '',
            requested_preload: preload,
            current_preload: video.preload,
            has_src: Boolean(video.getAttribute('src')),
            has_data_src: Boolean(video.dataset.src)
        });
    }
}

/**
 * Release buffered media outside the active preload window. The URL remains
 * in data-src so revisiting the story can load it again through the spinner.
 */
function releaseFullscreenVideo(video) {
    const source = video.getAttribute('src');
    if (!source) return;

    video.pause();
    video.classList.remove('is-ready');
    video.removeAttribute('src');
    video.preload = 'none';
    video.load();
    storiesDebug('video.preload.released', {
        slide_story_id: video.closest('.fullscreen-story')?.dataset.storyId || '',
        released_src: source
    });
}

/**
 * Helper: Preload videos in sliding window
 */
function preloadNearbyFullscreenVideos(container, currentIndex) {
    const slides = container.querySelectorAll('.fullscreen-story:not(.fullscreen-loading)');
    // Give the active video full priority. Neighbor metadata warms the next
    // transition without downloading multiple full videos concurrently.
    const start = Math.max(0, currentIndex - 1);
    const end = Math.min(slides.length - 1, currentIndex + 1);
    storiesDebug('video.preload.window', {
        current_index: currentIndex,
        start_index: start,
        end_index: end,
        slide_count: slides.length
    });
    for (let i = 0; i < slides.length; i++) {
        const video = slides[i]?.querySelector('video');
        if (!video) continue;

        if (i >= start && i <= end) {
            preloadFullscreenVideo(video, i === currentIndex ? 'auto' : 'metadata');
        } else {
            releaseFullscreenVideo(video);
        }
    }
}

/**
 * Toggle Story Audio
 */
window.toggleStoryAudio = function (charId, event) {
    if (event) event.stopPropagation();

    StoriesState.isMuted = !StoriesState.isMuted;
    const context = storyContextFromElement(event?.currentTarget, charId);
    trackStoriesEvent('audio_changed', context.story, context.index, {
        audio_enabled: !StoriesState.isMuted,
        story_view_id: StoriesState.activeView?.viewId || ''
    });

    // Update all active story videos and icons
    document.querySelectorAll('.fullscreen-video').forEach(video => {
        video.muted = StoriesState.isMuted;
    });

    document.querySelectorAll('.audio-toggle').forEach(toggle => {
        if (StoriesState.isMuted) {
            toggle.classList.remove('unmuted');
        } else {
            toggle.classList.add('unmuted');
        }
    });


};

/**
 * Handle Story Like with Animation & Persistence
 */
window.handleStoryLike = function (charId, event) {
    if (event && event.stopPropagation) event.stopPropagation();
    if (!charId) return;

    const btn = event.currentTarget;
    if (!btn) return;

    const isActive = btn.classList.contains('is-active');
    const delta = isActive ? -1 : 1;
    const context = storyContextFromElement(btn, charId);

    // 1. Trigger State/Animation
    if (isActive) {
        btn.classList.remove('is-active');
        // Remove from likedIds
        StoriesState.likedIds = StoriesState.likedIds.filter(id => id !== String(charId));
    } else {
        btn.classList.add('is-active');
        triggerHeartExplosion(btn);
        // Add to likedIds
        if (!StoriesState.likedIds.includes(String(charId))) {
            StoriesState.likedIds.push(String(charId));
        }
        // Remove from disliked if liked
        StoriesState.dislikedIds = StoriesState.dislikedIds.filter(id => id !== String(charId));
        const dislikeBtn = btn.parentElement.querySelector('.dislike-btn');
        if (dislikeBtn) {
            dislikeBtn.classList.remove('is-active');
            const dislikeIcon = dislikeBtn.querySelector('i');
            if (dislikeIcon) {
                dislikeIcon.classList.remove('fa-solid');
                dislikeIcon.classList.add('fa-regular');
            }
        }
    }

    // Update Local Storage
    safeStorage.local.set('growtype_liked_stories', JSON.stringify(StoriesState.likedIds));
    safeStorage.local.set('growtype_disliked_stories', JSON.stringify(StoriesState.dislikedIds));
    trackStoriesEvent('like_changed', context.story, context.index, {
        is_liked: !isActive,
        story_view_id: StoriesState.activeView?.viewId || ''
    });

    // Sync Like Record to Server (if logged in)
    if (document.body.classList.contains('logged-in')) {
        const formData = new FormData();
        formData.append('action', 'growtype_record_story_like_personal');
        formData.append('character_id', charId);
        formData.append('is_liked', !isActive ? '1' : '0');
        navigator.sendBeacon(StoriesState.ajaxUrl, formData);
    }

    // 3. State Management
    StoriesState.pendingLikes[charId] = (StoriesState.pendingLikes[charId] || 0) + delta;

    // 4. Debounced Backend Sync
    if (StoriesState.likeSyncTimers[charId]) {
        clearTimeout(StoriesState.likeSyncTimers[charId]);
    }

    StoriesState.likeSyncTimers[charId] = setTimeout(() => {
        syncLikesToBackend(charId);
    }, 2000);


};

/**
 * Heart Explosion Animation
 */
function triggerHeartExplosion(element) {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const heartEmojis = ['❤️', '💖', '💝', '💕', '💘'];
    const count = 10;

    for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'heart-particle';
        particle.innerHTML = heartEmojis[Math.floor(Math.random() * heartEmojis.length)];

        // Random trajectory
        const tx = (Math.random() - 0.5) * 300; // -150 to 150
        const ty = (Math.random() * -300) - 50; // -50 to -350 (upwards)
        const s = 0.5 + Math.random(); // scale 0.5 to 1.5
        const r = (Math.random() - 0.5) * 90; // rotate -45 to 45

        particle.style.left = `${centerX}px`;
        particle.style.top = `${centerY}px`;
        particle.style.setProperty('--tx', `${tx}px`);
        particle.style.setProperty('--ty', `${ty}px`);
        particle.style.setProperty('--s', s);
        particle.style.setProperty('--r', `${r}deg`);

        document.body.appendChild(particle);

        // Remove after animation
        setTimeout(() => particle.remove(), 1000);
    }
}

/**
 * Sync Pending Likes to Backend
 */
function syncLikesToBackend(charId) {
    const count = StoriesState.pendingLikes[charId];
    if (!count || count <= 0) return;



    const formData = new FormData();
    formData.append('action', 'growtype_child_record_likes');
    formData.append('character_id', charId);
    formData.append('count', count);

    // Reset pending count BEFORE sending to avoid race conditions with quick clicks
    StoriesState.pendingLikes[charId] = 0;

    fetch(StoriesState.ajaxUrl, {
        method: 'POST',
        body: formData
    })
        .then(res => res.json())
        .then(data => {
            if (data.success) {

            }
        })
        .catch(err => {

            // Put count back if failed? Maybe not to avoid infinite loop on bad data
        });
}
/**
 * Handle Story Dislike
 */
window.handleStoryDislike = function (charId, event) {
    if (event && event.stopPropagation) event.stopPropagation();
    if (!charId) return;

    const btn = event.currentTarget;
    if (!btn) return;

    const isActive = btn.classList.contains('is-active');
    const delta = isActive ? -1 : 1;
    const context = storyContextFromElement(btn, charId);

    // 1. Trigger State/Animation
    const icon = btn.querySelector('i');
    if (isActive) {
        btn.classList.remove('is-active');
        if (icon) {
            icon.classList.remove('fa-solid');
            icon.classList.add('fa-regular');
        }
        // Remove from dislikedIds
        StoriesState.dislikedIds = StoriesState.dislikedIds.filter(id => id !== String(charId));
    } else {
        btn.classList.add('is-active');
        if (icon) {
            icon.classList.remove('fa-regular');
            icon.classList.add('fa-solid');
        }
        // Add to dislikedIds
        if (!StoriesState.dislikedIds.includes(String(charId))) {
            StoriesState.dislikedIds.push(String(charId));
        }
        // Remove from liked if disliked
        StoriesState.likedIds = StoriesState.likedIds.filter(id => id !== String(charId));

        // Un-like if liked
        const likeBtn = btn.parentElement.querySelector('.like-btn');
        if (likeBtn && likeBtn.classList.contains('is-active')) {
            window.handleStoryLike(charId, { currentTarget: likeBtn, stopPropagation: () => { } });
        }
    }

    // Update Local Storage
    safeStorage.local.set('growtype_disliked_stories', JSON.stringify(StoriesState.dislikedIds));
    safeStorage.local.set('growtype_liked_stories', JSON.stringify(StoriesState.likedIds));
    trackStoriesEvent('dislike_changed', context.story, context.index, {
        is_disliked: !isActive,
        story_view_id: StoriesState.activeView?.viewId || ''
    });

    // Sync Dislike Record to Server (if logged in)
    if (document.body.classList.contains('logged-in')) {
        const formData = new FormData();
        formData.append('action', 'growtype_record_story_dislike');
        formData.append('character_id', charId);
        formData.append('is_disliked', !isActive ? '1' : '0');
        navigator.sendBeacon(StoriesState.ajaxUrl, formData);
    }

    // 2. State Management (for global count)
    StoriesState.pendingDislikes[charId] = (StoriesState.pendingDislikes[charId] || 0) + delta;

    // 3. Debounced Backend Sync
    if (StoriesState.dislikeSyncTimers[charId]) {
        clearTimeout(StoriesState.dislikeSyncTimers[charId]);
    }

    StoriesState.dislikeSyncTimers[charId] = setTimeout(() => {
        syncDislikesToBackend(charId);
    }, 2000);
};

/**
 * Sync Pending Dislikes to Backend
 */
function syncDislikesToBackend(charId) {
    const count = StoriesState.pendingDislikes[charId];
    if (count === 0) return;

    const formData = new FormData();
    formData.append('action', 'growtype_child_record_dislikes');
    formData.append('character_id', charId);
    formData.append('count', count);

    // Reset pending count BEFORE sending
    StoriesState.pendingDislikes[charId] = 0;

    fetch(StoriesState.ajaxUrl, {
        method: 'POST',
        body: formData
    })
        .then(res => res.json())
        .catch(err => {
            storiesDebug('dislike.sync_failed', {
                character_id: String(charId || ''),
                message: err?.message || String(err)
            });
            console.error('Failed to sync dislikes:', err);
        });
}
